import { UserPlus, Camera, ShieldCheck, CreditCard, Truck, Star, ShoppingBag, Store } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEPS = [
  { icon: UserPlus, title: "Create an account", text: "Sign up free as a buyer. Want to sell? Apply to become a verified seller." },
  { icon: ShieldCheck, title: "Get verified", text: "Our team manually reviews every seller to keep the marketplace 100% authentic." },
  { icon: Camera, title: "List your item", text: "Upload your own photos, write a description, set your price for new or used beauty." },
  { icon: CreditCard, title: "Secure checkout", text: "Buyers pay online. Funds are held safely in escrow — nobody gets scammed." },
  { icon: Truck, title: "Ship it", text: "Seller ships the item and marks it shipped. Buyer confirms once it arrives." },
  { icon: Star, title: "Release & review", text: "Payment is released to the seller (minus 15% fee). Buyers leave star reviews." },
];

const FAQS = [
  { q: "Is Beauty Trade Club free to use?", a: "Yes — creating an account and browsing the marketplace is completely free for buyers. Sellers only pay a 15% fee on completed sales, which covers verification, escrow protection, secure payments and support. There are no listing fees." },
  { q: "How do I know sellers and products are authentic?", a: "Every seller is manually reviewed and approved by our team before they can list. Buyers also leave honest 5-star reviews after each purchase, so the community keeps quality high. If an item is misrepresented, our escrow protection has you covered." },
  { q: "What is escrow protection?", a: "When you buy an item, your payment is held securely by Beauty Trade Club instead of going straight to the seller. The seller is only paid after you confirm the item arrived as described — or automatically seven days after it ships. If there's a problem, you can report an issue and our team reviews it before any funds are released." },
  { q: "Can I sell used makeup and skincare?", a: "Absolutely. You can sell new, unopened products as well as gently-used items. Just be honest in your listing — include clear photos, note how much a product has been used, and mention any expiry or period-after-opening (PAO) details so buyers know exactly what they're getting." },
  { q: "How and when do sellers get paid?", a: "Sellers connect a Stripe account to receive payouts. After a buyer confirms delivery, 85% of the sale is released to the seller's connected account. Beauty Trade Club keeps a 15% platform fee (5% when the sale comes through an influencer referral)." },
  { q: "What are Influencer Picks?", a: "Approved influencers can curate their favorite products into 'Picks' and share them. When someone buys through an influencer's pick, the influencer earns a 10% commission, the seller keeps 85%, and the platform fee drops to 5%. You can apply to become an influencer from your dashboard." },
  { q: "How do returns work?", a: "Returns are initiated after the buyer receives the item. For standard returns the buyer covers return shipping. If a listing was misrepresented, the seller covers both the return shipping and the full price. See our Return Policy page for full details." },
  { q: "Is my payment information secure?", a: "Yes. All payments are processed securely through Stripe, an industry-leading payment provider. Beauty Trade Club never stores your full card details." },
];

export default function HowItWorks() {
  const navigate = useNavigate();
  usePageMeta(
    "How It Works — Buy, Sell & Trade Beauty | Beauty Trade Club",
    "Learn how to buy, sell and trade makeup safely on Beauty Trade Club — verified sellers, multi-photo listings and escrow-protected payments every step of the way.",
    "/how-it-works"
  );
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-schema";
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="text-center mb-14">
        <h1 className="font-display text-4xl sm:text-5xl font-black">How It Works</h1>
        <p className="text-neutral-500 mt-4 max-w-2xl mx-auto text-lg">
          Beauty Trade Club makes buying, selling and trading beauty safe and simple — protected by escrow every step
          of the way. Here's everything you need to know to get started, whether you're shopping for a deal or clearing
          out your vanity.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-2xl p-6 hover:border-[#E8956B]/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-[#E8956B]/15 flex items-center justify-center mb-4">
              <s.icon className="text-[#E8956B]" size={22} />
            </div>
            <div className="text-xs font-bold text-[#E8956B] mb-1">STEP {i + 1}</div>
            <h3 className="font-bold text-lg mb-2">{s.title}</h3>
            <p className="text-sm text-neutral-500 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-12">
        <div className="bg-white border border-neutral-200 rounded-2xl p-7">
          <ShoppingBag className="text-[#E8956B] mb-3" />
          <h2 className="font-display text-xl font-bold mb-3">For buyers</h2>
          <p className="text-neutral-600 leading-relaxed">
            Browse thousands of authentic new and gently-used beauty products from verified sellers. Filter by category,
            brand or condition, read honest reviews, and check out securely. Your payment is protected by escrow, so if
            an item never arrives or isn't as described, you're covered. Once your order lands, simply confirm receipt —
            or open a dispute if something's wrong. It's beauty shopping without the risk.
          </p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-2xl p-7">
          <Store className="text-[#E8956B] mb-3" />
          <h2 className="font-display text-xl font-bold mb-3">For sellers</h2>
          <p className="text-neutral-600 leading-relaxed">
            Turn the products you no longer use into cash. Apply to become a verified seller, connect your Stripe account,
            and list items in minutes with your own photos and descriptions. When your item sells, you keep 85% of the
            sale — paid out automatically once the buyer confirms delivery. No listing fees, no hassle, just a simple way
            to give your beauty a second life.
          </p>
        </div>
      </div>

      <div className="mt-12 text-center bg-gradient-to-r from-[#E8956B]/10 to-[#E8956B]/5 border border-[#E8956B]/20 rounded-3xl p-10">
        <h2 className="font-display text-2xl font-bold">Escrow Protection, Always</h2>
        <p className="text-neutral-600 mt-3 max-w-2xl mx-auto">
          When you buy, your money is held securely. The seller is only paid after you confirm the item arrived as
          described. Beauty Trade Club takes a 15% fee on each completed sale to fund verification, secure payments and
          support that keep the community safe.
        </p>
      </div>

      {/* FAQ */}
      <div className="mt-16">
        <h2 className="font-display text-3xl font-black text-center mb-8">Frequently Asked Questions</h2>
        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`faq-item-${i}`} className="group bg-white border border-neutral-200 rounded-2xl px-6 py-4 open:border-[#E8956B]/50 transition-colors">
              <summary className="flex items-center justify-between cursor-pointer font-semibold text-neutral-900 list-none">
                {f.q}
                <span className="text-[#E8956B] text-2xl leading-none transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-neutral-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-14 text-center">
        <div className="flex flex-wrap justify-center gap-3">
          <button data-testid="hiw-shop-btn" onClick={() => navigate("/shop")} className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-7 py-3 btn-glow transition-all">
            Start Shopping
          </button>
          <button data-testid="hiw-sell-btn" onClick={() => navigate("/sell")} className="border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-7 py-3 transition-all">
            Become a Seller
          </button>
        </div>
      </div>
    </div>
  );
}
